import { STORAGE_KEY as ACTING_AS_STORAGE_KEY } from './Context/ActingAsContext.js';
import errorReporter from './services/errorReporter.js';
import NotificationManager from './Components/Notification2/NotificationManager.js';
import { getErrorMessage } from './helpers/errorMessages.js';

/**
 * Attach request/response interceptors to an axios instance.
 * Kept in a tracked file (not .gitignored) so interceptors are deployed
 * together with the rest of the frontend, regardless of per-env baseURL config
 * in axiosInstance.js.
 */

// Error codes that mean the session itself is dead (expired token, IP
// mismatch after VPN/network change, missing token) — as opposed to a
// permission error on an otherwise-valid session (insufficientRightsError).
// For these we show a persistent tooltip/notification and bounce the user
// to /login so they can re-authenticate; otherwise background autosave calls
// (e.g. report draft saving) fail silently with a 403 and nobody notices
// until work is lost.
const SESSION_EXPIRED_CODES = ['AuthorizationError', 'NoAuthorizationTokenError', 'IPAddressMismatchError'];

let redirectingToLogin = false;

function attachToken(config) {
    let token;
    try {
        token = localStorage.getItem('token');
    } catch (e) {
        return;
    }
    if (!token) return;

    const headers = config.headers;
    const existing = headers && (
        (typeof headers.get === 'function' && (headers.get('Authorization') || headers.get('authorization')))
        || headers.Authorization
        || headers.authorization
    );
    if (existing) return;

    const value = 'Bearer ' + token;
    if (headers && typeof headers.set === 'function') {
        headers.set('Authorization', value);
    } else {
        config.headers = { ...(headers || {}), Authorization: value };
    }
}

function redirectToLogin() {
    if (redirectingToLogin) return;
    if (typeof window === 'undefined') return;
    if (window.location.pathname.startsWith('/login')) return;

    redirectingToLogin = true;

    try {
        localStorage.removeItem('token');
    } catch (e) {
        // ignore storage errors
    }

    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?next=${next}`;
}

export default function applyInterceptors(instance) {
    instance.interceptors.request.use((config) => {
        attachToken(config);
        try {
            const url = config.url || '';
            const isSubmission =
                url.startsWith('/api/submission/') ||
                url.includes('/api/submission/');

            if (!isSubmission) return config;

            const raw = sessionStorage.getItem(ACTING_AS_STORAGE_KEY);
            if (!raw) return config;

            const parsed = JSON.parse(raw);
            const actingAsRespondentID = parsed && parsed.respondent_id;
            if (!actingAsRespondentID) return config;

            config.params = { ...(config.params || {}), actingAsRespondentID };

            const method = (config.method || 'get').toLowerCase();
            if (method === 'post' || method === 'put' || method === 'patch') {
                const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
                if (isFormData) {
                    if (!config.data.has('actingAsRespondentID')) {
                        config.data.append('actingAsRespondentID', actingAsRespondentID);
                    }
                } else if (config.data && typeof config.data === 'object') {
                    config.data = { ...config.data, actingAsRespondentID };
                } else if (config.data == null) {
                    config.data = { actingAsRespondentID };
                }
            }
        } catch (e) {
            // fall through silently
        }
        return config;
    });

    instance.interceptors.response.use(
        (response) => response,
        (error) => {
            try {
                const url = error.config?.url || '';
                const status = error.response?.status;
                const errorCode = error.response?.data?.errorCode;

                if (!url.includes('/api/errors')) {
                    errorReporter.report({
                        message: errorCode || `API ${error.config?.method?.toUpperCase()} ${url} → ${status || 'network error'}`,
                        stack: error.stack || null,
                        component: null,
                        level: status >= 500 ? 'error' : 'warning',
                        metadata: {
                            type: 'api_error',
                            method: error.config?.method,
                            url,
                            status,
                            errorCode,
                        },
                    });
                }

                // Dead session (expired token / IP mismatch after VPN switch /
                // missing token): surface a tooltip and send the user back to
                // /login instead of letting requests (esp. background autosave)
                // fail silently.
                if (status === 403 && SESSION_EXPIRED_CODES.includes(errorCode) && !url.startsWith('/api/account/login')) {
                    NotificationManager.error(
                        getErrorMessage(errorCode),
                        'Сессия завершена',
                        10000
                    );
                    redirectToLogin();
                }
            } catch (_e) {
                // Never break error propagation
            }
            return Promise.reject(error);
        }
    );
}
